import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { format, addDays, addMonths, addYears, formatISO } from "date-fns";
import { useEffect, useState, useCallback } from "react";
import { SubscriptionPlan, UserSubscription } from "@/types/subscription";
import { Currency } from "@/types/payment";
import { PromoDiscount } from "@/types/subscription";
import { DialogFooter } from "@/components/ui/dialog";
import { validatePromoCode } from "@/api/subscription-plans";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Check,
  X,
  Tag,
  Loader2,
  Gift,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { getUserReferralInfo } from "@/api/referrals";
import { useQuery } from "@tanstack/react-query";

// Use the imported PaymentMethod type with optional description
type PaymentMethod = {
  id: number;
  name: string;
  description?: string;
  isActive: boolean;
  supportedCurrencies: string[];
};

const subscriptionFormSchema = z.object({
  planId: z.number({ required_error: "Please select a subscription plan" }),
  startDate: z.date().optional().default(new Date()),
  price: z.number().min(0, "Price cannot be negative"),
  currencyCode: z.string().min(1, "Please select a currency"),
  paymentMethodId: z.union([z.number(), z.literal("none"), z.null()]),
  autoRenew: z.boolean().default(false),
  status: z.string().optional().default("active"),
  adminNotes: z.string().optional(),
  promoCode: z.string().optional(),
  promotionId: z.number().nullish(),
  totalDiscount: z.number().optional().default(0), // Total combined discount
  promoDiscount: z.number().optional().default(0), // Specific promotion discount
  referralDiscount: z.number().optional().default(0), // Specific referral discount
  useReferralDiscount: z.boolean().optional().default(false), // Whether to apply referral discount
});

type UserSubscriptionFormProps = {
  userId: number;
  userName: string;
  currentSubscription: UserSubscription | null | undefined;
  subscriptionPlans: SubscriptionPlan[];
  currencies: Currency[];
  paymentMethods: PaymentMethod[];
  isProcessing: boolean;
  onSubmit: (data: SubscriptionFormData) => void;
  onCancel: () => void;
  promotions?: PromoDiscount[]; // Accept promotions prop
};

export type SubscriptionFormData = z.infer<typeof subscriptionFormSchema>;

export default function UserSubscriptionForm({
  userId,
  userName,
  currentSubscription,
  subscriptionPlans,
  currencies,
  paymentMethods,
  isProcessing,
  onSubmit,
  onCancel,
  promotions,
}: UserSubscriptionFormProps) {
  // Component state
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(
    null
  );
  const [selectedCurrency, setSelectedCurrency] = useState<string>("USD");
  const [selectedPaymentMethod, setSelectedPaymentMethod] =
    useState<PaymentMethod | null>(null);
  const [isValidatingPromo, setIsValidatingPromo] = useState(false);
  const [promoMessage, setPromoMessage] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);
  const [appliedDiscount, setAppliedDiscount] = useState<{
    id: number;
    code: string;
    value: number;
    type: "percentage" | "fixed";
    originalPrice: number;
    discountedPrice: number;
  } | null>(null);
  const [basePrice, setBasePrice] = useState<number>(0);
  const [originalPrice, setOriginalPrice] = useState<number>(0); // Store the original price before any discounts
  const [planDiscount, setPlanDiscount] = useState<number>(0); // Store plan discount amount
  const [referralAvailable, setReferralAvailable] = useState<boolean>(false);
  const [referralDiscount, setReferralDiscount] = useState<number>(0);
  const [showDetailedBreakdown, setShowDetailedBreakdown] =
    useState<boolean>(false);
  const [showAdvancedOptions, setShowAdvancedOptions] =
    useState<boolean>(false);

  // Check if user has a referrer (for discount eligibility)
  const { data: referralInfo } = useQuery({
    queryKey: ["userReferralInfo", userId],
    queryFn: () => getUserReferralInfo(userId),
    enabled: !!userId,
  });

  // Initialize the form
  const subscriptionForm = useForm<SubscriptionFormData>({
    resolver: zodResolver(subscriptionFormSchema),
    defaultValues: {
      startDate: new Date(),
      currencyCode: "INR", // Default to INR currency
      autoRenew: false,
      status: "active",
      promoCode: "",
      promotionId: undefined,
      totalDiscount: 0,
      promoDiscount: 0,
      referralDiscount: 0,
      useReferralDiscount: false,
      adminNotes: currentSubscription
        ? `Modifying subscription for ${userName}`
        : `New subscription for ${userName}`,
    },
  });

  // Effect to set referral discount availability
  useEffect(() => {
    if (referralInfo?.referredBy) {
      setReferralAvailable(true);
      // Default to using referral discount if available and no promotion is applied
      const hasPromotion = subscriptionForm.getValues("promotionId");
      if (!hasPromotion) {
        subscriptionForm.setValue("useReferralDiscount", true);
      }
    }
  }, [referralInfo, subscriptionForm]);

  // Helper function to calculate referral discount (10% of original plan price)
  const calculateReferralDiscount = (originalPlanPrice: number): number => {
    return parseFloat((originalPlanPrice * 0.1).toFixed(2));
  };

  // Helper function to apply or remove referral discount
  const handleReferralDiscountToggle = (applyDiscount: boolean) => {
    if (!referralAvailable || !selectedPlan) return;

    if (applyDiscount) {
      // Clear any promotion first
      subscriptionForm.setValue("promoCode", "");
      subscriptionForm.setValue("promotionId", undefined);
      subscriptionForm.setValue("promoDiscount", 0);
      setAppliedDiscount(null);
      setPromoMessage(null);

      // Apply referral discount based on original plan price
      const discount = calculateReferralDiscount(originalPrice);
      setReferralDiscount(discount);
      subscriptionForm.setValue("referralDiscount", discount);
      subscriptionForm.setValue("useReferralDiscount", true);

      // Update price and total discount
      const discountedPrice = parseFloat((basePrice - discount).toFixed(2));
      subscriptionForm.setValue("price", discountedPrice);
      subscriptionForm.setValue("totalDiscount", planDiscount + discount);

      // Update admin notes
      const currentNotes = subscriptionForm.getValues("adminNotes") || "";
      if (!currentNotes.includes("referral discount")) {
        subscriptionForm.setValue(
          "adminNotes",
          `${
            currentNotes ? currentNotes + " - " : ""
          }10% referral discount (₹${discount}) applied`
        );
      }
    } else {
      // Remove referral discount
      setReferralDiscount(0);
      subscriptionForm.setValue("referralDiscount", 0);
      subscriptionForm.setValue("useReferralDiscount", false);

      // Reset price to base price
      subscriptionForm.setValue("price", basePrice);
      subscriptionForm.setValue("totalDiscount", planDiscount);

      // Update admin notes to remove referral mention
      const currentNotes = subscriptionForm.getValues("adminNotes") || "";
      const updatedNotes = currentNotes.replace(
        /\s*-\s*10% referral discount.*?applied/,
        ""
      );
      subscriptionForm.setValue("adminNotes", updatedNotes);
    }
  };

  // Helper function to handle plan change
  const handlePlanChange = (planId: number) => {
    const plan = subscriptionPlans.find((p) => p.id === planId);
    if (plan) {
      setSelectedPlan(plan);

      // Set default price based on selected plan
      if (plan.priceConfigurations && plan.priceConfigurations.length > 0) {
        // Auto-select INR currency on plan change
        const targetCurrency = "INR";
        setSelectedCurrency(targetCurrency);
        subscriptionForm.setValue("currencyCode", targetCurrency);

        // Find a price configuration for INR currency
        let priceConfig = plan.priceConfigurations.find(
          (pc) => pc.currencyCode === targetCurrency
        );

        // If INR not available, fall back to selected currency or first available
        if (!priceConfig) {
          priceConfig =
            plan.priceConfigurations.find(
              (pc) => pc.currencyCode === selectedCurrency
            ) || plan.priceConfigurations[0];
          setSelectedCurrency(priceConfig.currencyCode);
          subscriptionForm.setValue("currencyCode", priceConfig.currencyCode);
        }

        if (priceConfig) {
          // Get original price from the price configuration
          const originalPriceValue = priceConfig.price;
          setOriginalPrice(originalPriceValue);

          // Calculate price after applying plan discount
          let price = originalPriceValue;
          let planDiscountAmount = 0;

          // Apply plan discount if any
          if (plan.planDiscountPercentage && plan.planDiscountPercentage > 0) {
            // Calculate discount amount and round to 2 decimal places
            planDiscountAmount = parseFloat(
              ((price * plan.planDiscountPercentage) / 100).toFixed(2)
            );
            price = parseFloat((price - planDiscountAmount).toFixed(2));
            setPlanDiscount(planDiscountAmount);
          } else {
            setPlanDiscount(0);
          }

          // Store the base price (without promo discount)
          setBasePrice(price);

          // Set total discount (initially just the plan discount)
          subscriptionForm.setValue("totalDiscount", planDiscountAmount);

          // If there's a promo discount applied, recalculate
          if (appliedDiscount) {
            // Clear any previously applied discount since plan changed
            setAppliedDiscount(null);
            subscriptionForm.setValue("promoCode", "");
            subscriptionForm.setValue("promotionId", undefined);
            subscriptionForm.setValue("promoDiscount", 0);
            setPromoMessage(null);
          }

          // Check if referral discount should be applied by default
          if (referralAvailable && !appliedDiscount) {
            const referralDiscountAmount =
              calculateReferralDiscount(originalPriceValue);
            setReferralDiscount(referralDiscountAmount);
            subscriptionForm.setValue(
              "referralDiscount",
              referralDiscountAmount
            );
            subscriptionForm.setValue("useReferralDiscount", true);

            const finalPrice = parseFloat(
              (price - referralDiscountAmount).toFixed(2)
            );
            subscriptionForm.setValue("price", finalPrice);
            subscriptionForm.setValue(
              "totalDiscount",
              planDiscountAmount + referralDiscountAmount
            );
          } else {
            subscriptionForm.setValue("price", price);
            subscriptionForm.setValue("referralDiscount", 0);
            subscriptionForm.setValue("useReferralDiscount", false);
            setReferralDiscount(0);
          }

          // Add a note about discount in admin notes if not already present
          const currentNotes = subscriptionForm.getValues("adminNotes") || "";
          if (
            plan.planDiscountPercentage > 0 &&
            !currentNotes.includes("discount")
          ) {
            subscriptionForm.setValue(
              "adminNotes",
              `${currentNotes ? currentNotes + " - " : ""}${
                plan.planDiscountPercentage
              }% plan discount (${getCurrencySymbol(
                selectedCurrency
              )}${planDiscountAmount}) applied`
            );
          }
        } else {
          // Use the first available price configuration if no match for currency
          const firstPriceConfig = plan.priceConfigurations[0];

          // Get original price from first configuration
          const originalPriceValue = firstPriceConfig.price;
          setOriginalPrice(originalPriceValue);

          // Calculate price after applying plan discount
          let price = originalPriceValue;
          let planDiscountAmount = 0;

          // Apply discount if any
          if (plan.planDiscountPercentage && plan.planDiscountPercentage > 0) {
            planDiscountAmount = parseFloat(
              ((price * plan.planDiscountPercentage) / 100).toFixed(2)
            );
            price = parseFloat((price - planDiscountAmount).toFixed(2));
            setPlanDiscount(planDiscountAmount);
          } else {
            setPlanDiscount(0);
          }

          // Store the base price (without promo discount)
          setBasePrice(price);

          // Set total discount
          subscriptionForm.setValue("totalDiscount", planDiscountAmount);

          // Clear any previously applied discount
          setAppliedDiscount(null);
          subscriptionForm.setValue("promoCode", "");
          subscriptionForm.setValue("promotionId", undefined);
          subscriptionForm.setValue("promoDiscount", 0);
          setPromoMessage(null);

          subscriptionForm.setValue("price", price);
          setSelectedCurrency(firstPriceConfig.currencyCode);
          subscriptionForm.setValue(
            "currencyCode",
            firstPriceConfig.currencyCode
          );
        }
      }

      // Set plan ID in form
      subscriptionForm.setValue("planId", plan.id);

      // Don't auto-select any payment method by default, let user choose
      setSelectedPaymentMethod(null);
      subscriptionForm.setValue("paymentMethodId", "none");

      // Set a default admin note if none exists
      if (!subscriptionForm.getValues("adminNotes")) {
        subscriptionForm.setValue("adminNotes", `Assigned ${plan.name} plan`);
      }
    }
  };

  // Handle currency change
  const handleCurrencyChange = (currencyCode: string) => {
    setSelectedCurrency(currencyCode);
    subscriptionForm.setValue("currencyCode", currencyCode);

    // Update payment method options based on new currency
    if (selectedPlan && selectedPlan.priceConfigurations) {
      // Find a price configuration for the new currency
      const priceConfig = selectedPlan.priceConfigurations.find(
        (pc) => pc.currencyCode === currencyCode
      );

      if (priceConfig) {
        let price = priceConfig.price;

        // Apply plan discount if any
        if (
          selectedPlan.planDiscountPercentage &&
          selectedPlan.planDiscountPercentage > 0
        ) {
          // Calculate discount amount and round to 2 decimal places
          const discountAmount =
            (price * selectedPlan.planDiscountPercentage) / 100;
          price = parseFloat((price - discountAmount).toFixed(2));
        }

        // Store the base price (without promo discount)
        setBasePrice(price);

        // Clear any previously applied discount
        setAppliedDiscount(null);
        subscriptionForm.setValue("promoCode", "");
        subscriptionForm.setValue("promotionId", undefined);
        setPromoMessage(null);

        subscriptionForm.setValue("price", price);
      }
    }

    // Keep the payment method setting as is, don't automatically change it
    // This preserves user's selection when changing currency
  };

  // Handle promo code validation
  const handleValidatePromo = async () => {
    const promoCode = subscriptionForm.getValues("promoCode");
    const planId = subscriptionForm.getValues("planId");

    if (!promoCode || !planId) {
      setPromoMessage({
        type: "error",
        message: !promoCode
          ? "Please enter a promo code"
          : "Please select a plan first",
      });
      return;
    }

    setIsValidatingPromo(true);
    setPromoMessage(null);

    try {
      // Use local promotions array instead of API call
      const promo = promotions?.find(
        (p) =>
          p.promoCode?.toLowerCase() === promoCode.toLowerCase() &&
          p.isActive &&
          (!p.applicablePlans?.length || p.applicablePlans.includes(planId))
      );

      // console.log("Validating promo code:", promoCode);
      // console.log("Selected plan ID:", planId);
      // console.log("Found promo:", promo);
      // console.log("All promotions:", promotions);

      if (!promo) {
        setPromoMessage({
          type: "error",
          message: "Invalid promo code or not applicable to selected plan",
        });
        setAppliedDiscount(null);
        subscriptionForm.setValue("promotionId", undefined);
        subscriptionForm.setValue("promoDiscount", 0); // Reset promo discount
        // Keep the plan discount as the total discount
        subscriptionForm.setValue("totalDiscount", planDiscount);

        // Reset price to base price
        subscriptionForm.setValue("price", basePrice);

        return;
      }

      // Validate date range
      const now = new Date();
      const startDate = new Date(promo.startDate);
      const endDate = promo.endDate ? new Date(promo.endDate) : null;

      if (now < startDate || (endDate && now > endDate)) {
        setPromoMessage({
          type: "error",
          message: "This promotion has expired or is not yet active",
        });
        return;
      }

      // Calculate discounted price and the actual promo discount amount
      let discountedPrice = basePrice;
      let promoDiscountAmount = 0;

      if (promo.discountType === "percentage") {
        promoDiscountAmount = parseFloat(
          ((basePrice * promo.discountValue) / 100).toFixed(2)
        );
        discountedPrice = parseFloat(
          (basePrice - promoDiscountAmount).toFixed(2)
        );
      } else {
        // Fixed amount discount
        promoDiscountAmount = parseFloat(
          Math.min(basePrice, promo.discountValue).toFixed(2)
        );
        discountedPrice = Math.max(0, basePrice - promoDiscountAmount);
      }

      // Calculate total discount (plan discount + promo discount)
      const totalDiscount = parseFloat(
        (planDiscount + promoDiscountAmount).toFixed(2)
      );

      // Clear referral discount if applied
      if (subscriptionForm.getValues("useReferralDiscount")) {
        subscriptionForm.setValue("referralDiscount", 0);
        subscriptionForm.setValue("useReferralDiscount", false);
        setReferralDiscount(0);
      }

      // Apply the discount
      setAppliedDiscount({
        id: promo.id,
        code: promoCode,
        value: promo.discountValue,
        type: promo.discountType,
        originalPrice: originalPrice,
        discountedPrice: discountedPrice,
      });

      // Update the form values
      subscriptionForm.setValue("price", discountedPrice);
      subscriptionForm.setValue("promotionId", promo.id);
      subscriptionForm.setValue("promoDiscount", promoDiscountAmount); // Save the specific promotion discount
      subscriptionForm.setValue("totalDiscount", totalDiscount); // Save total combined discount

      // Update admin notes
      const currentNotes = subscriptionForm.getValues("adminNotes") || "";
      if (!currentNotes.includes("promo code")) {
        subscriptionForm.setValue(
          "adminNotes",
          `${
            currentNotes ? currentNotes + " - " : ""
          }Promo code "${promoCode}" applied with ${
            promo.discountType === "percentage"
              ? `${promo.discountValue}% off (${getCurrencySymbol(
                  selectedCurrency
                )}${promoDiscountAmount})`
              : `${getCurrencySymbol(
                  selectedCurrency
                )}${promoDiscountAmount} off`
          }`
        );
      }

      setPromoMessage({
        type: "success",
        message: `Promo code applied! ${
          promo.discountType === "percentage"
            ? `${promo.discountValue}% off (${getCurrencySymbol(
                selectedCurrency
              )}${promoDiscountAmount})`
            : `${getCurrencySymbol(selectedCurrency)}${promoDiscountAmount} off`
        }`,
      });
    } catch (error) {
      console.error("Error validating promo code:", error);
      setPromoMessage({
        type: "error",
        message: "Error validating promo code. Please try again.",
      });
    } finally {
      setIsValidatingPromo(false);
    }
  };

  // Clear promo code and reset price
  const handleClearPromo = () => {
    subscriptionForm.setValue("promoCode", "");
    subscriptionForm.setValue("promotionId", undefined);
    subscriptionForm.setValue("promoDiscount", 0); // Reset promo discount
    setPromoMessage(null);
    setAppliedDiscount(null);

    // Check if referral discount should be re-applied
    if (referralAvailable) {
      const referralDiscountAmount = calculateReferralDiscount(basePrice);
      setReferralDiscount(referralDiscountAmount);
      subscriptionForm.setValue("referralDiscount", referralDiscountAmount);
      subscriptionForm.setValue("useReferralDiscount", true);

      const finalPrice = parseFloat(
        (basePrice - referralDiscountAmount).toFixed(2)
      );
      subscriptionForm.setValue("price", finalPrice);
      subscriptionForm.setValue(
        "totalDiscount",
        planDiscount + referralDiscountAmount
      );
    } else {
      // Reset to base price without any discount
      subscriptionForm.setValue("price", basePrice);
      subscriptionForm.setValue("totalDiscount", planDiscount);
    }

    // Update admin notes to remove promo code mention
    const currentNotes = subscriptionForm.getValues("adminNotes") || "";
    const updatedNotes = currentNotes.replace(
      /\s*-\s*Promo code ".*" applied.*/,
      ""
    );
    subscriptionForm.setValue("adminNotes", updatedNotes);
  };

  // Helper function to get currency symbol
  const getCurrencySymbol = (currencyCode: string): string => {
    const currency = currencies.find((c) => c.code === currencyCode);
    return (
      currency?.symbol ||
      (currencyCode === "INR"
        ? "₹"
        : currencyCode === "USD"
        ? "$"
        : currencyCode)
    );
  };

  // Initialize default currency when component mounts
  useEffect(() => {
    if (currencies.length > 0) {
      // Try to find INR in the currencies
      const inrCurrency = currencies.find((c) => c.code === "INR");

      // Set INR if available, otherwise use the first currency
      const defaultCurrency = inrCurrency
        ? inrCurrency.code
        : currencies[0].code;
      setSelectedCurrency(defaultCurrency);
      subscriptionForm.setValue("currencyCode", defaultCurrency);

      // If we already have a selected plan, update the price based on the currency
      if (selectedPlan) {
        handlePlanChange(selectedPlan.id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currencies, subscriptionForm, selectedPlan]);

  return (
    <Form {...subscriptionForm}>
      <form
        onSubmit={subscriptionForm.handleSubmit(onSubmit)}
        className="space-y-3"
      >
        {/* Compact Top Section - Plan and Currency in one row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {/* Subscription Plan - 2/3 width */}
          <FormField
            control={subscriptionForm.control}
            name="planId"
            render={({ field }) => (
              <FormItem className="md:col-span-2">
                <FormLabel>Subscription Plan</FormLabel>
                <Select
                  onValueChange={(value) =>
                    handlePlanChange(parseInt(value, 10))
                  }
                  defaultValue={field.value?.toString()}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a plan" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {subscriptionPlans.map((plan) => (
                      <SelectItem key={plan.id} value={plan.id.toString()}>
                        {plan.name} ({plan.duration} {plan.durationType})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Currency - 1/3 width */}
          <FormField
            control={subscriptionForm.control}
            name="currencyCode"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Currency</FormLabel>
                <Select
                  onValueChange={handleCurrencyChange}
                  value={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Currency" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {currencies.map((currency) => (
                      <SelectItem key={currency.code} value={currency.code}>
                        {currency.code}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Hidden start date field */}
        <FormField
          control={subscriptionForm.control}
          name="startDate"
          render={({ field }) => (
            <input
              type="hidden"
              value={field.value.toISOString()}
              onChange={(e) => field.onChange(new Date(e.target.value))}
            />
          )}
        />

        {/* Compact Pricing Card */}
        {selectedPlan && originalPrice > 0 && (
          <Card className="border-muted bg-muted/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <h4 className="text-sm font-medium">
                    {selectedPlan.name} ({selectedPlan.duration}{" "}
                    {selectedPlan.durationType})
                  </h4>
                  {selectedPlan.planDiscountPercentage > 0 && (
                    <Badge variant="secondary" className="text-xs">
                      {selectedPlan.planDiscountPercentage}% off
                    </Badge>
                  )}
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold text-green-600">
                    {getCurrencySymbol(selectedCurrency)}
                    {(subscriptionForm.getValues("price") || 0).toFixed(2)}
                  </div>
                  {(planDiscount > 0 ||
                    appliedDiscount ||
                    subscriptionForm.getValues("useReferralDiscount")) && (
                    <div className="text-xs text-muted-foreground line-through">
                      {getCurrencySymbol(selectedCurrency)}
                      {originalPrice.toFixed(2)}
                    </div>
                  )}
                </div>
              </div>

              {/* Quick Discount Summary */}
              {(planDiscount > 0 ||
                appliedDiscount ||
                subscriptionForm.getValues("useReferralDiscount")) && (
                <div className="flex flex-wrap gap-1 mb-2">
                  {planDiscount > 0 && (
                    <Badge
                      variant="outline"
                      className="text-xs text-orange-600"
                    >
                      Plan: -{getCurrencySymbol(selectedCurrency)}
                      {planDiscount.toFixed(2)}
                    </Badge>
                  )}
                  {appliedDiscount &&
                    subscriptionForm.getValues("promoDiscount") > 0 && (
                      <Badge
                        variant="outline"
                        className="text-xs text-blue-600"
                      >
                        Promo: -{getCurrencySymbol(selectedCurrency)}
                        {(
                          subscriptionForm.getValues("promoDiscount") || 0
                        ).toFixed(2)}
                      </Badge>
                    )}
                  {subscriptionForm.getValues("useReferralDiscount") &&
                    referralDiscount > 0 && (
                      <Badge
                        variant="outline"
                        className="text-xs text-green-600"
                      >
                        Referral: -{getCurrencySymbol(selectedCurrency)}
                        {referralDiscount.toFixed(2)}
                      </Badge>
                    )}
                </div>
              )}

              {/* Collapsible Detailed Breakdown */}
              <Collapsible
                open={showDetailedBreakdown}
                onOpenChange={setShowDetailedBreakdown}
              >
                <CollapsibleTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 p-0 text-xs text-muted-foreground hover:text-foreground"
                  >
                    {showDetailedBreakdown ? (
                      <>
                        Hide details <ChevronUp className="h-3 w-3 ml-1" />
                      </>
                    ) : (
                      <>
                        Show pricing breakdown{" "}
                        <ChevronDown className="h-3 w-3 ml-1" />
                      </>
                    )}
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="space-y-2 mt-2">
                  <div className="space-y-1 text-xs">
                    <div className="flex justify-between">
                      <span>Full Plan Price</span>
                      <span className="font-medium">
                        {getCurrencySymbol(selectedCurrency)}
                        {originalPrice.toFixed(2)}
                      </span>
                    </div>

                    {planDiscount > 0 && (
                      <div className="flex justify-between text-orange-600">
                        <span>
                          Standard Plan Discount (
                          {selectedPlan.planDiscountPercentage}%)
                        </span>
                        <span>
                          – {getCurrencySymbol(selectedCurrency)}
                          {planDiscount.toFixed(2)}
                        </span>
                      </div>
                    )}

                    {appliedDiscount &&
                      subscriptionForm.getValues("promoDiscount") > 0 && (
                        <div className="flex justify-between text-blue-600">
                          <span>
                            Promo Code Discount (
                            {appliedDiscount.type === "percentage"
                              ? `${appliedDiscount.value}%`
                              : "Fixed"}
                            )
                          </span>
                          <span>
                            – {getCurrencySymbol(selectedCurrency)}
                            {(
                              subscriptionForm.getValues("promoDiscount") || 0
                            ).toFixed(2)}
                          </span>
                        </div>
                      )}

                    {subscriptionForm.getValues("useReferralDiscount") &&
                      referralDiscount > 0 && (
                        <div className="flex justify-between text-green-600">
                          <span>Referral Discount (10%)</span>
                          <span>
                            – {getCurrencySymbol(selectedCurrency)}
                            {referralDiscount.toFixed(2)}
                          </span>
                        </div>
                      )}

                    <hr className="border-muted-foreground/20" />
                    <div className="flex justify-between font-medium">
                      <span>Final Price</span>
                      <span className="text-green-600">
                        {getCurrencySymbol(selectedCurrency)}
                        {(subscriptionForm.getValues("price") || 0).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </CardContent>
          </Card>
        )}

        {/* Discount Options Section */}
        <div className="space-y-3">
          {/* Referral Discount */}
          {referralAvailable && (
            <FormField
              control={subscriptionForm.control}
              name="useReferralDiscount"
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-center justify-between p-3 border rounded-lg bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
                    <div className="flex items-center space-x-3">
                      <Gift className="h-4 w-4 text-green-600" />
                      <div>
                        <div className="text-sm font-medium text-green-800">
                          Referral Discount (10% off)
                        </div>
                        <div className="text-xs text-green-600">
                          Referred by {referralInfo?.referredBy?.name}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Badge
                        variant="outline"
                        className="bg-green-100 text-green-700 border-green-300 text-xs"
                      >
                        ₹{referralDiscount.toFixed(2)} off
                      </Badge>
                      <input
                        type="checkbox"
                        checked={field.value}
                        onChange={(e) => {
                          field.onChange(e.target.checked);
                          handleReferralDiscountToggle(e.target.checked);
                        }}
                        disabled={!!appliedDiscount || isProcessing}
                        className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
                      />
                    </div>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          {/* Promo Code */}
          <FormField
            control={subscriptionForm.control}
            name="promoCode"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm">Promo Code (Optional)</FormLabel>
                <div className="flex gap-2">
                  <FormControl>
                    <div className="relative flex-1">
                      <Tag className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder={
                          subscriptionForm.getValues("useReferralDiscount")
                            ? "Remove referral discount to use promo code"
                            : "Enter promo code"
                        }
                        {...field}
                        className="pl-8 h-9"
                        disabled={
                          isValidatingPromo ||
                          isProcessing ||
                          subscriptionForm.getValues("useReferralDiscount")
                        }
                      />
                    </div>
                  </FormControl>
                  {appliedDiscount ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleClearPromo}
                      disabled={isProcessing}
                      className="shrink-0 h-9"
                    >
                      <X className="h-4 w-4 mr-1" />
                      Clear
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      onClick={handleValidatePromo}
                      size="sm"
                      disabled={
                        !field.value ||
                        isValidatingPromo ||
                        isProcessing ||
                        !selectedPlan ||
                        subscriptionForm.getValues("useReferralDiscount")
                      }
                      className="shrink-0 h-9"
                    >
                      {isValidatingPromo ? (
                        <>
                          <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                          Apply
                        </>
                      ) : (
                        "Apply"
                      )}
                    </Button>
                  )}
                </div>
                {promoMessage && (
                  <Alert
                    variant={
                      promoMessage.type === "success"
                        ? "default"
                        : "destructive"
                    }
                    className="py-1 px-2 text-xs mt-1"
                  >
                    <div className="flex items-center">
                      {promoMessage.type === "success" ? (
                        <Check className="h-3 w-3 mr-1" />
                      ) : (
                        <X className="h-3 w-3 mr-1" />
                      )}
                      {promoMessage.message}
                    </div>
                  </Alert>
                )}
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Hidden status field */}
        <FormField
          control={subscriptionForm.control}
          name="status"
          render={({ field }) => (
            <input type="hidden" {...field} value="active" />
          )}
        />

        {/* Hidden promotionId field */}
        <FormField
          control={subscriptionForm.control}
          name="promotionId"
          render={({ field }) => (
            <input
              type="hidden"
              value={field.value || ""}
              onChange={field.onChange}
            />
          )}
        />

        {/* Hidden total_discount field */}
        <FormField
          control={subscriptionForm.control}
          name="totalDiscount"
          render={({ field }) => (
            <input
              type="hidden"
              value={field.value || 0}
              onChange={field.onChange}
            />
          )}
        />

        {/* Hidden promo_discount field */}
        <FormField
          control={subscriptionForm.control}
          name="promoDiscount"
          render={({ field }) => (
            <input
              type="hidden"
              value={field.value || 0}
              onChange={field.onChange}
            />
          )}
        />

        {/* Hidden referral_discount field */}
        <FormField
          control={subscriptionForm.control}
          name="referralDiscount"
          render={({ field }) => (
            <input
              type="hidden"
              value={field.value || 0}
              onChange={field.onChange}
            />
          )}
        />

        {/* Hidden useReferralDiscount field */}
        <FormField
          control={subscriptionForm.control}
          name="useReferralDiscount"
          render={({ field }) => (
            <input
              type="hidden"
              value={field.value || false}
              onChange={field.onChange}
            />
          )}
        />

        {/* Advanced Options - Collapsible */}
        <Collapsible
          open={showAdvancedOptions}
          onOpenChange={setShowAdvancedOptions}
        >
          <CollapsibleTrigger asChild>
            <Button
              variant="ghost"
              className="h-8 p-0 text-sm text-muted-foreground hover:text-foreground justify-start"
            >
              {showAdvancedOptions ? (
                <>
                  Hide payment & admin options{" "}
                  <ChevronUp className="h-4 w-4 ml-1" />
                </>
              ) : (
                <>
                  Show payment & admin options{" "}
                  <ChevronDown className="h-4 w-4 ml-1" />
                </>
              )}
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="space-y-3 mt-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {/* Payment Method */}
              <FormField
                control={subscriptionForm.control}
                name="paymentMethodId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm">Payment Method</FormLabel>
                    <Select
                      onValueChange={(value) => {
                        field.onChange(
                          value === "none" ? "none" : parseInt(value, 10)
                        );
                      }}
                      defaultValue={
                        field.value === null
                          ? "none"
                          : field.value?.toString() || "none"
                      }
                    >
                      <FormControl>
                        <SelectTrigger className="h-9">
                          <SelectValue placeholder="Select payment method" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="none">
                          None (Manual Subscription)
                        </SelectItem>
                        {paymentMethods
                          .filter((pm) =>
                            pm.supportedCurrencies.includes(selectedCurrency)
                          )
                          .map((method) => (
                            <SelectItem
                              key={method.id}
                              value={String(method.id)}
                            >
                              {method.name}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Admin Notes */}
              <FormField
                control={subscriptionForm.control}
                name="adminNotes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm">Admin Notes</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Add any special notes"
                        {...field}
                        className="text-xs h-9"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Auto Renew */}
              <FormField
                control={subscriptionForm.control}
                name="autoRenew"
                render={({ field }) => (
                  <FormItem className="md:col-span-2">
                    <div className="flex items-center space-x-2 rounded-md border p-2">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <FormLabel className="text-sm font-normal">
                        Enable Auto-Renewal
                      </FormLabel>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </CollapsibleContent>
        </Collapsible>

        {/* Hidden Price Field for Form Validation */}
        <FormField
          control={subscriptionForm.control}
          name="price"
          render={({ field }) => <input type="hidden" {...field} />}
        />

        <DialogFooter className="pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isProcessing}
            size="sm"
          >
            Cancel
          </Button>
          <Button type="submit" disabled={isProcessing} size="sm">
            {isProcessing ? (
              <>
                <span className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-primary border-t-transparent"></span>
                Saving...
              </>
            ) : (
              "Save Changes"
            )}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}
